/**
 * RTL (Right-to-Left) Text Detection Utility
 *
 * Supports detection of RTL languages including:
 * - Arabic (U+0600 to U+06FF)
 * - Hebrew (U+0590 to U+05FF)
 * - Persian/Farsi (U+0600 to U+06FF)
 * - Urdu (U+0600 to U+06FF)
 * - Syriac (U+0700 to U+074F)
 */

/**
 * RTL language Unicode ranges
 */
const RTL_UNICODE_RANGES = [
  /[\u0600-\u06FF]/, // Arabic, Persian, Urdu
  /[\u0590-\u05FF]/, // Hebrew
  /[\u0700-\u074F]/, // Syriac
  /[\u0780-\u07BF]/, // Thaana (Maldivian)
  /[\u08A0-\u08FF]/, // Arabic Extended-A
  /[\uFB50-\uFDFF]/, // Arabic Presentation Forms-A
  /[\uFE70-\uFEFF]/, // Arabic Presentation Forms-B
];

/**
 * Detects if the given text contains RTL characters
 *
 * @param text - The text to analyze
 * @returns true if RTL characters are detected, false otherwise
 */
export function isRTL(text: string): boolean {
  if (!text || text.trim().length === 0) {
    return false;
  }

  return RTL_UNICODE_RANGES.some((pattern) => pattern.test(text));
}

/**
 * Detects if the given text is primarily Arabic
 *
 * @param text - The text to analyze
 * @returns true if Arabic characters are detected, false otherwise
 */
export function isArabic(text: string): boolean {
  const arabicPattern = /[\u0600-\u06FF]/;
  return arabicPattern.test(text);
}

/**
 * Detects if the given text is primarily Hebrew
 *
 * @param text - The text to analyze
 * @returns true if Hebrew characters are detected, false otherwise
 */
export function isHebrew(text: string): boolean {
  const hebrewPattern = /[\u0590-\u05FF]/;
  return hebrewPattern.test(text);
}

/**
 * Gets the text direction based on content analysis
 *
 * @param text - The text to analyze
 * @returns "rtl" if RTL text is detected, "ltr" otherwise
 */
export function getTextDirection(text: string): 'rtl' | 'ltr' {
  return isRTL(text) ? 'rtl' : 'ltr';
}

/**
 * Gets the text alignment based on content analysis
 *
 * @param text - The text to analyze
 * @returns "right" if RTL text is detected, "left" otherwise
 */
export function getTextAlign(text: string): 'right' | 'left' {
  return isRTL(text) ? 'right' : 'left';
}

/**
 * Calculates the percentage of RTL characters in text
 * Useful for mixed-language content
 *
 * @param text - The text to analyze
 * @returns percentage of RTL characters (0-100)
 */
export function getRTLPercentage(text: string): number {
  if (!text || text.trim().length === 0) {
    return 0;
  }

  const totalChars = text.replace(/\s/g, '').length; // Exclude whitespace
  if (totalChars === 0) return 0;

  let rtlCount = 0;
  for (const char of text) {
    if (RTL_UNICODE_RANGES.some((pattern) => pattern.test(char))) {
      rtlCount++;
    }
  }

  return (rtlCount / totalChars) * 100;
}

/**
 * Determines if text should be rendered RTL based on a threshold
 * Useful for mixed-language content
 *
 * @param text - The text to analyze
 * @param threshold - Percentage threshold (default: 30%)
 * @returns true if RTL percentage exceeds threshold
 */
export function shouldUseRTL(text: string, threshold: number = 30): boolean {
  return getRTLPercentage(text) >= threshold;
}
